import type { Show, ShowHistory, Track } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { isNonNil, strongFromEntries } from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import type { StoreApi } from 'zustand';
import { supabaseApi } from '../api/api-store';
import type { TrackState } from './types';

export type TrackShowHistoryApi = ReturnType<typeof TrackShowHistoryApi>;

export const TrackShowHistoryApi = (
  get: StoreApi<TrackState>['getState'],
  set: StoreApi<TrackState>['setState'],
) =>
  ({
    fetchShowHistoriesForShow: async (showId: Uuid<Show>) => {
      const loading = get().loading;
      const showHistories = get().showHistories;

      console.log(
        'fetching show histories for show...',
        loading.showHistoriesForShow[showId],
        showHistories,
      );
      if (isNonNil(showHistories[showId])) {
        return;
      }

      if (loading.showHistoriesForShow[showId]) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              showHistoriesForShow: {
                ...s.loading.showHistoriesForShow,
                [showId]: true,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const { data: showHistoriesForShow } = await supabaseApi()
        .from('show_histories_public')
        .select('*')
        .eq('show_id', showId)
        .eq('show_type', 'show')
        .order('created_at', { ascending: false });

      if (isNonNil(showHistoriesForShow)) {
        set(
          (s) =>
            ({
              showHistories: {
                ...s.showHistories,
                [showId]: showHistoriesForShow,
              },
            }) as const satisfies Partial<TrackState>,
        );
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              showHistoriesForShow: {
                ...s.loading.showHistoriesForShow,
                [showId]: false,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
    fetchTrackHistoryForShowHistory: async (showHistoryId: Uuid<ShowHistory>) => {
      const loading = get().loading;
      const tracksByShowHistory = get().trackHistoryByShowHistory;
      if (isNonNil(tracksByShowHistory[showHistoryId])) {
        return;
      }

      if (loading.tracksForShowHistory[showHistoryId]) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              tracksForShowHistory: {
                ...s.loading.tracksForShowHistory,
                [showHistoryId]: true,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const { data: recentTrackHistories } = await supabaseApi()
        .from('track_history_recent_public')
        .select('*')
        .eq('show_history_id', showHistoryId)
        .order('created_at', { ascending: false });

      const { data: fullTrackHistories } = await supabaseApi()
        .from('track_history_full_public')
        .select('*')
        .eq('show_history_id', showHistoryId)
        .order('created_at', { ascending: false });

      const allTrackIdsSet = new Set(
        [...(recentTrackHistories ?? []), ...(fullTrackHistories ?? [])]
          .map((s) => s.track_id)
          .filter(isNonNil),
      );

      const allTrackHistories = [
        ...(recentTrackHistories ?? []),
        ...(fullTrackHistories ?? []),
      ] as const;
      const uniqueTrackHistories = allTrackHistories.filter((trackHistory, index) => {
        const firstIndex = allTrackHistories.findIndex((a) => a.id === trackHistory.id);
        return index === firstIndex;
      });

      const allTrackIdsFromShowHistory = [...allTrackIdsSet] as const;

      const tracksById = get().tracksById;
      const trackIdsToFetch = allTrackIdsFromShowHistory.filter((id) => isNil(tracksById[id]));

      const { data: tracks } = await (async () => {
        if (isEmpty(trackIdsToFetch)) {
          return {
            data: [],
          };
        }
        return supabaseApi().from('tracks').select('*').in('id', trackIdsToFetch);
      })();

      const newTracksById = strongFromEntries((tracks ?? []).map((t) => [t.id, t]));

      set(
        (s) =>
          ({
            trackHistoryByShowHistory: {
              ...s.trackHistoryByShowHistory,
              [showHistoryId]: [...uniqueTrackHistories],
            },
            tracksById: {
              ...s.tracksById,
              ...newTracksById,
            },
            loading: {
              ...s.loading,
              tracksForShowHistory: {
                ...s.loading.tracksForShowHistory,
                [showHistoryId]: false,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
    fetchShowsFeaturedByTrack: async (trackId: Uuid<Track>) => {
      const loading = get().loading.showsFeaturedByTrack[trackId];
      if (loading) {
        return;
      }

      const showsFeaturedByTrack = get().showsFeaturedByTrack[trackId];
      if (isNonNil(showsFeaturedByTrack)) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              showsFeaturedByTrack: {
                ...s.loading,
                [trackId]: true,
              },
            },
          }) as const satisfies Partial<TrackState>,
      );

      const showIdsFromRecentTracks = await supabaseApi()
        .from('track_history_recent_public')
        .select('id,airing_show_id')
        .eq('track_id', trackId)
        .not('airing_show_id', 'is', null)
        .order('created_at', {
          ascending: false,
        });

      const showIdsFromFullTracks = await supabaseApi()
        .from('track_history_full_public')
        .select('id,airing_show_id')
        .eq('track_id', trackId)
        .not('airing_show_id', 'is', null)
        .not('id', 'in', `(${showIdsFromRecentTracks.data?.map((s) => s.id).join(',')})`)
        .order('created_at', {
          ascending: false,
        });

      const allShowIds = [
        ...(showIdsFromRecentTracks.data ?? []),
        ...(showIdsFromFullTracks.data ?? []),
      ];

      const uniqueShows = [
        ...new Set(allShowIds.map((s) => s.airing_show_id).filter(isNonNil)).values(),
      ];

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              showsFeaturedByTrack: {
                ...s.loading,
                [trackId]: false,
              },
            },
            showsFeaturedByTrack: {
              ...s.showsFeaturedByTrack,
              [trackId]: uniqueShows,
            },
          }) as const satisfies Partial<TrackState>,
      );
    },
  }) as const;
